import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ── Privat-Modus ──────────────────────────────────────────────────────────────
// Globaler Schalter, der personenbezogene Finanz-Werte (Beträge & Coin-Mengen)
// durch Punkte (••••) ersetzt — für Screenshots/Streaming. Der echte Wert wird
// im maskierten Zustand GAR NICHT ins DOM gerendert (siehe <Private>), lässt
// sich also weder per Inspector aufdecken noch aus einem Screenshot lesen.
//
// Persistiert in localStorage (wie Theme/Währung) und spiegelt sich zusätzlich
// als `html[data-private="on|off"]` — so können einzelne Stellen, an denen
// Punkte keinen Sinn ergeben (z. B. Verlaufs-Charts), per CSS weichgezeichnet
// werden (.priv-blur in index.css).

const PrivacyContext = createContext({
  privateMode: false,
  setPrivateMode: () => {},
  toggle: () => {},
});

export function PrivacyProvider({ children }) {
  const [privateMode, setPrivateMode] = useState(() => {
    try { return localStorage.getItem('red_private_mode') === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('red_private_mode', privateMode ? '1' : '0'); } catch {}
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-private', privateMode ? 'on' : 'off');
    }
  }, [privateMode]);

  const toggle = useCallback(() => setPrivateMode((p) => !p), []);

  return (
    <PrivacyContext.Provider value={{ privateMode, setPrivateMode, toggle }}>
      {children}
    </PrivacyContext.Provider>
  );
}

export const usePrivacy = () => useContext(PrivacyContext);

// useMaskText — für Fälle, in denen kein JSX-Knoten geht (z. B. Werte, die in
// i18n-Interpolation `t('…', { amount })` als String landen — ein React-Element
// würde dort zu „[object Object]"). Gibt eine Funktion zurück, die den String im
// Privat-Modus durch Punkte ersetzt, sonst unverändert lässt.
export function useMaskText() {
  const { privateMode } = useContext(PrivacyContext);
  return (s) => (privateMode ? '••••' : s);
}
